import { useState, useMemo, useEffect, useRef } from 'react'
import { Play, Square } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useSupabaseQuery, formatDate, formatTime, formatDuration } from '../hooks/useSupabase'
import { Button, Card, StatCard, Badge, PageHeader } from '../components/ui/SharedUI'
import { ClockInModal } from '../components/ClockInModal'
import { supabase } from '../lib/supabase'

export function DashboardPage() {
  const { user, profile } = useAuth()
  const { data: shifts, loading, refetch } = useSupabaseQuery('shifts', { orderBy: 'clock_in', ascending: false })
  const { data: recipients } = useSupabaseQuery('recipients', { column: 'owner_id' })
  const { data: notes } = useSupabaseQuery('evidence_notes', { orderBy: 'created_at', ascending: false })

  // Only a confirmed clock-in counts as the active shift. An unconfirmed draft
  // (button pressed, modal not finished) is handled by the clock-in modal below.
  const activeShift = shifts.find(s => s.is_active && s.confirmed_at)
  const recipientMap = Object.fromEntries(recipients.map(r => [r.id, r.name]))

  const [clockInOpen, setClockInOpen] = useState(false)
  const [pendingInitiatedAt, setPendingInitiatedAt] = useState(null)
  const [resumeDraft, setResumeDraft] = useState(null)
  const resumeHandledRef = useRef(null)

  // If a clock-in was started but never finished (crash, closed tab, navigation),
  // pick the draft back up so the caregiver can finish or discard it.
  useEffect(() => {
    if (clockInOpen) return
    const draft = shifts.find(s => s.is_active && !s.confirmed_at)
    if (draft && resumeHandledRef.current !== draft.id) {
      resumeHandledRef.current = draft.id
      setResumeDraft(draft)
      setClockInOpen(true)
    }
  }, [shifts, clockInOpen])

  const stats = useMemo(() => {
    const now = new Date()
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
    const startOfWeek = new Date(now)
    startOfWeek.setDate(now.getDate() - now.getDay())
    startOfWeek.setHours(0, 0, 0, 0)

    const completed = shifts.filter(s => s.clock_out && !s.is_active)
    const loggedCompleted = completed.filter(s => s.authorship !== 'observed')
    const observedCompleted = completed.filter(s => s.authorship === 'observed')

    const monthShifts = loggedCompleted.filter(s => new Date(s.clock_in) >= startOfMonth)
    const weekShifts = loggedCompleted.filter(s => new Date(s.clock_in) >= startOfWeek)
    const monthObserved = observedCompleted.filter(s => new Date(s.clock_in) >= startOfMonth)

    const calcHours = (arr) => arr.reduce((sum, s) => sum + (new Date(s.clock_out) - new Date(s.clock_in)) / 3600000, 0)

    return {
      monthHours: calcHours(monthShifts),
      weekHours: calcHours(weekShifts),
      directCare: calcHours(monthShifts.filter(s => s.shift_type === 'direct_care')),
      noShows: monthShifts.filter(s => s.shift_type === 'no_show_covered').length,
      totalShifts: monthShifts.length,
      observedThisMonth: monthObserved.length,
      observedHoursThisMonth: calcHours(monthObserved),
    }
  }, [shifts])

  const heatmapData = useMemo(() => {
    const days = []
    const now = new Date()
    for (let i = 27; i >= 0; i--) {
      const d = new Date(now)
      d.setDate(now.getDate() - i)
      const dateStr = d.toISOString().split('T')[0]
      const dayShifts = shifts.filter(s => s.date === dateStr && s.clock_out && !s.is_active && s.authorship !== 'observed')
      const hours = dayShifts.reduce((sum, s) => sum + (new Date(s.clock_out) - new Date(s.clock_in)) / 3600000, 0)
      days.push({ dateStr, hours })
    }
    return days
  }, [shifts])

  function heatColor(hours) {
    if (hours === 0) return '#EDF2F7'
    if (hours <= 2) return '#C6F6D5'
    if (hours <= 4) return '#9AE6B4'
    if (hours <= 6) return '#68D391'
    if (hours <= 8) return '#48BB78'
    return '#2F855A'
  }

  function startClockIn() {
    if (activeShift) return
    setResumeDraft(null)
    setPendingInitiatedAt(new Date().toISOString())
    setClockInOpen(true)
  }

  async function handleClockOut() {
    if (!activeShift) return
    await supabase.from('shifts').update({ clock_out: new Date().toISOString(), is_active: false }).eq('id', activeShift.id)
    refetch()
  }

  const recentShifts = shifts.filter(s => !s.is_active).slice(0, 5)
  const recentNotes = notes.slice(0, 3)

  if (loading) return <div style={{ padding: '2rem', color: '#718096' }}>Loading...</div>

  return (
    <div style={{ padding: '2rem', maxWidth: '1200px' }}>
      <PageHeader
        title={`Welcome, ${profile?.name?.split(' ')[0] || 'there'}`}
        description={new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        action={activeShift
          ? <Button variant="coral" onClick={handleClockOut}><Square size={16} /> Clock out</Button>
          : <Button onClick={startClockIn}><Play size={16} /> Clock in</Button>
        }
      />

      {activeShift && <ActiveBanner shift={activeShift} name={recipientMap[activeShift.recipient_id]} onOut={handleClockOut} />}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '1.5rem' }}>
        <StatCard label="This month" value={`${stats.monthHours.toFixed(1)}h`} sub={`${stats.totalShifts} shifts`} />
        <StatCard label="This week" value={`${stats.weekHours.toFixed(1)}h`} />
        <StatCard label="Direct care" value={`${stats.directCare.toFixed(1)}h`} />
        <StatCard label="No-shows" value={stats.noShows} color={stats.noShows > 0 ? '#E07856' : '#2C7A7B'} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
        <Card>
          <div style={{ fontSize: '13px', fontWeight: 500, color: '#718096', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Last 28 days</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '3px' }}>
            {heatmapData.map(day => (
              <div key={day.dateStr} title={`${day.dateStr}: ${day.hours.toFixed(1)}h`}
                style={{ aspectRatio: '1', borderRadius: '3px', background: heatColor(day.hours), minHeight: '20px' }} />
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
            <span style={{ fontSize: '11px', color: '#A0AEC0' }}>Less</span>
            <div style={{ display: 'flex', gap: '2px' }}>
              {[0, 2, 4, 6, 8, 10].map(h => <div key={h} style={{ width: '12px', height: '12px', borderRadius: '2px', background: heatColor(h) }} />)}
            </div>
            <span style={{ fontSize: '11px', color: '#A0AEC0' }}>More</span>
          </div>
        </Card>

        <Card>
          <div style={{ fontSize: '13px', fontWeight: 500, color: '#718096', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Recent evidence notes</div>
          {recentNotes.length === 0
            ? <div style={{ fontSize: '14px', color: '#A0AEC0', padding: '1rem 0' }}>No notes yet</div>
            : recentNotes.map(note => (
              <div key={note.id} style={{ padding: '8px 0', borderBottom: '1px solid #EDF2F7' }}>
                <div style={{ fontSize: '12px', color: '#A0AEC0', marginBottom: '2px' }}>{formatDate(note.date)}</div>
                <div style={{ fontSize: '14px', color: '#4A5568' }}>{note.text.length > 120 ? note.text.slice(0, 120) + '...' : note.text}</div>
              </div>
            ))
          }
        </Card>
      </div>

      <Card>
        <div style={{ fontSize: '13px', fontWeight: 500, color: '#718096', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Recent shifts</div>
        {recentShifts.length === 0
          ? <div style={{ fontSize: '14px', color: '#A0AEC0', padding: '1rem 0' }}>No shifts logged yet. Clock in to get started.</div>
          : <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #EDF2F7' }}>
                {['Date', 'Time', 'Duration', 'Type', 'Recipient'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 10px', fontSize: '12px', fontWeight: 500, color: '#A0AEC0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recentShifts.map(s => (
                <tr key={s.id} style={{ borderBottom: '1px solid #EDF2F7' }}>
                  <td style={{ padding: '10px', fontSize: '14px', color: '#4A5568' }}>{formatDate(s.date)}</td>
                  <td style={{ padding: '10px', fontSize: '14px', color: '#4A5568' }}>{formatTime(s.clock_in)} – {formatTime(s.clock_out)}</td>
                  <td style={{ padding: '10px', fontSize: '14px', color: '#4A5568' }}>{formatDuration(s.clock_in, s.clock_out)}</td>
                  <td style={{ padding: '10px' }}><Badge>{s.shift_type.replace(/_/g, ' ')}</Badge></td>
                  <td style={{ padding: '10px', fontSize: '14px', color: '#4A5568' }}>{recipientMap[s.recipient_id] || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        }
      </Card>

      {clockInOpen && (
        <ClockInModal
          user={user}
          recipients={recipients}
          defaultShiftType={profile?.in_waiting_period ? 'waiting_period' : 'direct_care'}
          initiatedAt={pendingInitiatedAt}
          existingDraft={resumeDraft}
          onClose={(changed) => { setClockInOpen(false); setResumeDraft(null); if (changed) refetch() }}
        />
      )}
    </div>
  )
}

function ActiveBanner({ shift, name, onOut }) {
  const [elapsed, setElapsed] = useState(0)
  useEffect(() => {
    const tick = () => setElapsed(Date.now() - new Date(shift.clock_in).getTime())
    tick()
    const i = setInterval(tick, 1000)
    return () => clearInterval(i)
  }, [shift.clock_in])

  const s = Math.floor(elapsed / 1000)
  const display = `${Math.floor(s / 3600).toString().padStart(2, '0')}:${Math.floor((s % 3600) / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`

  return (
    <Card style={{ marginBottom: '1rem', borderLeft: '3px solid #E07856', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#E07856', animation: 'pulse 2s infinite' }} />
        <div>
          <div style={{ fontSize: '14px', fontWeight: 500, color: '#1A202C' }}>Active shift</div>
          <div style={{ fontSize: '13px', color: '#718096' }}>Started {formatTime(shift.clock_in)}{name && ` · ${name}`}</div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '22px', fontWeight: 500, color: '#E07856' }}>{display}</span>
        <Button variant="coral" size="sm" onClick={onOut}><Square size={14} /> Clock out</Button>
      </div>
      <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }`}</style>
    </Card>
  )
}
